import { usePharmacyDrug, usePharmacyBatches } from '../../services/httpServices/queries';
import type { PharmacyDrug } from '../../services/httpServices/pharmacyDrugService';
import {
  X,
  Loader2,
  Package,
  Tags,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Info,
  Layers,
  Wallet,
} from 'lucide-react';

interface DrugDetailsModalProps {
  drugId: string;
  /** Partial data used to render immediately while the full record is fetched. */
  fallback?: Partial<PharmacyDrug> | null;
  onClose: () => void;
}

export function DrugDetailsModal({ drugId, fallback, onClose }: DrugDetailsModalProps) {
  const { data, isLoading } = usePharmacyDrug(drugId);
  const { data: batchesData, isLoading: batchesLoading } = usePharmacyBatches(drugId);

  const drug: Partial<PharmacyDrug> = data?.data ?? fallback ?? {};
  const batches = batchesData?.data || [];

  const currentStock = Number(fallback?.currentStock ?? drug.currentStock ?? 0);
  const isLowStock =
    fallback?.isLowStock ?? drug.isLowStock ?? currentStock <= Number(drug.reorderLevel ?? 0);

  const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString() : '—');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Package size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-text truncate">{drug.name ?? 'Drug'}</h2>
                {drug.itemType === 'ACCESSORY' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                    Accessory
                  </span>
                )}
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    drug.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                  }`}
                >
                  {drug.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                  {drug.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-xs font-mono text-text-muted mt-0.5">{drug.drugCode ?? '—'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-background text-text-muted shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-5 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Loader2 size={14} className="animate-spin" /> Loading full details…
            </div>
          )}

          {/* Stock summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-border bg-background p-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-text-muted uppercase">
                <Layers size={12} /> In Stock
              </div>
              <div className="mt-1 text-lg font-semibold text-text">
                {currentStock} <span className="text-xs font-normal text-text-muted">{drug.unitOfMeasure}</span>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-background p-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-text-muted uppercase">
                <Wallet size={12} /> Price
              </div>
              <div className="mt-1 text-lg font-semibold text-text">
                {drug.price !== undefined && drug.price !== null ? `৳${Number(drug.price).toFixed(2)}` : '—'}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-background p-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-text-muted uppercase">
                <AlertTriangle size={12} /> Stock Status
              </div>
              <div className="mt-1">
                {isLowStock ? (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">
                    Low (reorder at {drug.reorderLevel})
                  </span>
                ) : (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                    OK
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Identification */}
          <section className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
              <Info size={14} className="text-primary" /> Identification
            </h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
              <DetailField label="Drug Code" value={drug.drugCode} />
              <DetailField label="Form" value={drug.form} />
              <DetailField label="Strength" value={drug.strength} />
              <DetailField label="Item Type" value={drug.itemType} />
              <DetailField label="Unit of Measure" value={drug.unitOfMeasure} />
              <DetailField label="Reorder Quantity" value={drug.reorderQuantity} />
            </dl>
          </section>

          {/* Classification */}
          <section className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
              <Tags size={14} className="text-primary" /> Classification
            </h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
              <DetailField label="Generic Name" value={drug.genericName?.name} />
              <DetailField label="Category" value={drug.category?.name} />
              <DetailField label="Company" value={drug.company?.name} />
              <DetailField label="Storage Rack" value={drug.storageRack?.name} />
              <DetailField label="Created" value={formatDate(drug.createdAt)} />
            </dl>
          </section>

          {drug.description && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-text">Description</h3>
              <p className="text-sm text-text-muted whitespace-pre-wrap">{drug.description}</p>
            </section>
          )}

          {/* Batches */}
          <section className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
              <Calendar size={14} className="text-primary" /> Stock Batches
            </h3>
            {batchesLoading ? (
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <Loader2 size={14} className="animate-spin" /> Loading batches…
              </div>
            ) : batches.length === 0 ? (
              <p className="text-sm text-text-muted">No batches recorded for this drug.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-background border-b border-border">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Batch</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Expiry</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Remaining</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {batches.map((batch) => (
                      <tr key={batch.id}>
                        <td className="px-3 py-2 font-mono text-text">{batch.batchNumber}</td>
                        <td className="px-3 py-2 text-text-muted">
                          <span
                            className={
                              batch.isExpired
                                ? 'text-danger'
                                : batch.isExpiringSoon
                                  ? 'text-warning'
                                  : 'text-text-muted'
                            }
                          >
                            {formatDate(batch.expiryDate)}
                            {batch.daysToExpiry !== null && batch.daysToExpiry !== undefined && (
                              <span className="ml-1 text-xs">
                                ({batch.daysToExpiry < 0 ? 'expired' : `${batch.daysToExpiry}d`})
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-text">{Number(batch.quantityRemaining)}</td>
                        <td className="px-3 py-2 text-right text-text-muted">{Number(batch.quantityReceived)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-border">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-text-muted hover:text-text">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  const isEmpty = value === undefined || value === null || value === '';
  return (
    <div className="space-y-0.5 min-w-0">
      <dt className="text-xs font-medium text-text-muted uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-text break-words">
        {isEmpty ? <span className="text-text-muted/60">—</span> : value}
      </dd>
    </div>
  );
}
